import {
  getBoardId,
  getBoardViewId,
  getTenantId,
  getUserId,
} from './firebaseInit';
import { getDefaultConverter } from '../utils/helpers/firestore';
import { Retrospective } from '../@types/domain';
import {
  DocumentSnapshot,
  FirestoreError,
  Unsubscribe,
  addDoc,
  arrayRemove,
  arrayUnion,
  collection,
  doc,
  getDoc,
  getDocs,
  getFirestore,
  onSnapshot,
  orderBy,
  query,
  serverTimestamp,
  updateDoc,
  where,
} from 'firebase/firestore';
import { getFunctions, httpsCallable } from 'firebase/functions';

function getRetrospectiveCollection() {
  return collection(getFirestore(), 'retrospectives');
}

export function getRetrospectiveDocumentById(retrospectiveId) {
  return doc(
    getFirestore(),
    getRetrospectiveCollection().path,
    retrospectiveId
  );
}

const createRetrospective = async (
  name,
  facilitator,
  participants,
  format,
  customFormat,
  teamMoodQuestions,
  votesLimit
) => {
  const addedRetrospective = {
    tenantId: getTenantId(),
    boardId: getBoardId(),
    boardViewId: getBoardViewId(),
    name,
    facilitator,
    participants,
    format,
    votesCount: 0,
    actionItemsCount: 0,
    createdOn: serverTimestamp(),
    createdBy: getUserId(),
    stage: teamMoodQuestions ? 'TEAM MOOD' : 'THINK',
    deleted: false,
    cardsCount: 0,
    moodStarted: false,
    usersDoneWithCurrentStage: [],
    team: null,
    votesLimit,
    customFormat: null,
    teamMoodQuestions: null,
  };

  addedRetrospective.customFormat = customFormat ?? null;
  addedRetrospective.teamMoodQuestions = teamMoodQuestions ?? null;

  const documentReference = await addDoc(
    getRetrospectiveCollection(),
    addedRetrospective
  );

  return documentReference.id;
};

const getRetrospective = async (retrospectiveId) => {
  const document_ = await getDoc(getRetrospectiveDocumentById(retrospectiveId));
  return document_.exists()
    ? { id: document_.id, ...document_.data() }
    : undefined;
};

const getRetrospectives = async (search = '') => {
  const results: Retrospective[] = [];
  const tenantId = getTenantId();
  const boardId = getBoardId();
  const boardViewId = getBoardViewId();

  if (search === '') {
    const q = query(
      getRetrospectiveCollection(),
      where('tenantId', '==', tenantId),
      where('boardId', '==', boardId),
      where('boardViewId', '==', boardViewId),
      where('deleted', '==', false),
      orderBy('createdOn', 'desc')
    ).withConverter(getDefaultConverter<Retrospective>());

    const querySnapshot = await getDocs(q);
    for (const document_ of querySnapshot.docs) {
      results.push(document_.data());
    }
  } else {
    const searchFunction = httpsCallable<unknown, Retrospective[]>(
      getFunctions(),
      'searchRetrospectivesOnAlgolia'
    );

    const { data } = await searchFunction({
      searchQuery: search,
      boardId,
      boardViewId,
    });
    results.push(...data);
  }
  return results;
};

const updateRetrospective = async (id, fields) => {
  await updateDoc(getRetrospectiveDocumentById(id), fields);
};

const deleteRetrospective = async (retrospectiveId) => {
  await updateRetrospective(retrospectiveId, { deleted: true });
};

const setUserDoneStatus = async (id, action, attribute) => {
  await updateDoc(getRetrospectiveDocumentById(id), {
    usersDoneWithCurrentStage:
      action === 'add' ? arrayUnion(attribute) : arrayRemove(attribute),
  });
};

const listenRetrospectiveChange = (
  id: string,
  callback: (snapshot: DocumentSnapshot) => void,
  errorCallback: (error: FirestoreError) => void
): Unsubscribe => {
  return onSnapshot(getRetrospectiveDocumentById(id), callback, errorCallback);
};

export {
  createRetrospective,
  getRetrospective,
  getRetrospectives,
  updateRetrospective,
  setUserDoneStatus,
  listenRetrospectiveChange,
  deleteRetrospective,
};
